/**
 *
 * useClientResources
 *
 */

import { useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _uniqBy from 'lodash/uniqBy';
import { useQuery } from 'react-query';
import { getResultsVisibility } from 'containers/Assessment/utils';
import { parseProfileForClientResources } from 'containers/Auth/utils';
import getRegionBySlug from 'containers/Main/services/getRegionBySlug.gql';
import {
  makeSelectProfile,
  makeSelectProfileFilters,
} from 'containers/Auth/selectors';
import { makeSelectClientLanding } from 'containers/ClientLanding/selectors';
import {
  makeSelectClientDetails,
  makeSelectRegion,
} from 'containers/Main/selectors';
import useContentfulLocale from 'components/useContentfulLocale';
import { sortClientExcusives } from 'containers/TopicCenter/utils';
import { filterClientArticlesInResources } from 'components/Hooks/useExcludedArticleTypes';
import { RESOURCE_REVIEW_STATUSES } from 'utils/constants';
import GetLinkResources from './services/getLinkResources';
import getResourcesQueryFn from './services/getResources';
import getLinkedResources from './services/getLinkedResources';

const stateSelector = createStructuredSelector({
  clientDetails: makeSelectClientDetails(),
  clientLanding: makeSelectClientLanding(),
  profile: makeSelectProfile(),
  profileFilters: makeSelectProfileFilters(),
  region: makeSelectRegion(),
});

const flatContentfulResources = resources =>
  (resources || []).map(resource => ({
    ...resource.fields,
    sys: resource.sys,
  }));

const getAvailableResourceIds = resources =>
  resources
    ?.filter(
      ({ reviewStatus }) => reviewStatus === RESOURCE_REVIEW_STATUSES.accepted,
    )
    .map(resource => _get(resource, 'sys.id'))
    .filter(Boolean);

const queryConfig = {
  staleTime: 1000 * 60 * 60, // 1 hour
};

function useClientResources(ignoreProfileState = false) {
  const ctfLocaleFilter = useContentfulLocale();
  const {
    clientDetails,
    clientLanding,
    profile,
    profileFilters,
    region,
  } = useSelector(stateSelector);

  const {
    insuranceOptions,
    insuranceTags,
    requiredTagsOptions,
    state,
  } = profileFilters;
  const clientId = _get(clientDetails, 'sys.id');
  const {
    data: linkedResources,
    isFetching: isFetchingLinkedResources,
  } = useQuery(
    clientId && [
      'linked-client-resources',
      clientId,
      _get(clientDetails, 'clientGroup.sys.id'),
      ctfLocaleFilter,
    ],
    getLinkedResources,
    queryConfig,
  );
  // fetch region
  const { data: regionData, isFetching: isFetchingRegion } = useQuery(
    region && clientId && ['region', region],
    getRegionBySlug,
    queryConfig,
  );

  const {
    data: linkResources = [],
    isFetching: isFetchingLinkResources,
  } = useQuery(
    clientId && [
      'home-page-links',
      {
        clientId,
        clientGroupId: _get(clientDetails, 'clientGroup.sys.id'),
        ctfLocaleFilter,
        profileTags: _get(profileFilters, 'tags'),
        requiredTagsOptions,
        insuranceTags,
        insuranceOptions,
      },
    ],
    GetLinkResources,
    queryConfig,
  );

  const hasMultipleLanding =
    _get(clientDetails, 'landingLinksCollection.items', []).length > 1;
  const landingClientResources = hasMultipleLanding
    ? _get(clientLanding, 'clientResourcesCollection.items', [])
    : [];
  const clientResources = filterClientArticlesInResources(
    _get(clientDetails, 'clientResourcesCollection.items', []),
    clientDetails,
  );
  const regionClientResources = _get(
    regionData,
    'clientResourcesCollection.items',
    [],
  );

  const clientResourcesSysIds = getAvailableResourceIds(clientResources);

  // useQuery to get clientResources from contentful by sys.ids
  const {
    data: clientResourcesData,
    isFetching: isFetchingClientResources,
  } = useQuery(
    clientResourcesSysIds?.length && [
      'client-resources',
      {
        localeFilters: ctfLocaleFilter,
        sysIds: clientResourcesSysIds,
      },
    ],
    getResourcesQueryFn,
    queryConfig,
  );

  // Reginal Client Resources
  const regionClientResourcesSysIds = getAvailableResourceIds(
    regionClientResources,
  );

  // useQuery to get regionClientResources from contentful by sys.ids
  const {
    data: regionClientResourcesData,
    isFetching: isFetchingRegionClientResources,
  } = useQuery(
    regionClientResourcesSysIds?.length && [
      'region-client-resources',
      {
        localeFilters: ctfLocaleFilter,
        sysIds: regionClientResourcesSysIds,
      },
    ],
    getResourcesQueryFn,
    queryConfig,
  );

  // Landing Client Resources
  const landingClientResourcesSysIds = getAvailableResourceIds(
    landingClientResources,
  );

  // useQuery to get landingClientResources from contentful by sys.ids
  const {
    data: landingClientResourcesData,
    isFetching: isFetchingLandingClientResources,
  } = useQuery(
    landingClientResourcesSysIds?.length && [
      'landing-client-resources',
      {
        localeFilters: ctfLocaleFilter,
        sysIds: landingClientResourcesSysIds,
      },
    ],
    getResourcesQueryFn,
    queryConfig,
  );

  const finalClientResources = sortClientExcusives(
    _uniqBy(
      [
        ...flatContentfulResources(clientResourcesData),
        ...flatContentfulResources(regionClientResourcesData),
        ...flatContentfulResources(landingClientResourcesData),
        ...flatContentfulResources(linkedResources),
        ...linkResources,
      ],
      'sys.id',
    )
      .filter(item => _get(item, 'reviewStatus') === 'Accepted')
      .filter(item => {
        if (_get(item, 'visibility')) {
          const parsedProfile = parseProfileForClientResources(profile);
          return getResultsVisibility(
            _get(item, 'visibility'),
            parsedProfile,
            true,
          );
        }
        return true;
      })
      .filter(item => {
        if (ignoreProfileState) return true;

        const hasStateSet = Boolean(state) && !_isEmpty(_get(item, 'state'));

        if (!hasStateSet) return true;

        return _get(item, 'state').includes(state);
      }),
    [
      r => {
        const order = _get(r, 'resourceOrder');
        return !order ? Number.NEGATIVE_INFINITY : order;
      },
      r => {
        const order = _get(r, 'homeOrder');
        return !order ? Number.NEGATIVE_INFINITY : order;
      },
    ],
    ['asc', 'desc'],
  );

  return {
    clientResources: finalClientResources,
    isLoading:
      isFetchingClientResources ||
      isFetchingRegion ||
      isFetchingRegionClientResources ||
      isFetchingLandingClientResources ||
      isFetchingLinkedResources ||
      isFetchingLinkResources,
  };
}

export default useClientResources;
